import { debounce, orderBy } from 'lodash-es'
import type { Ref } from 'vue'
import { computed, onMounted, onUnmounted, ref, unref } from 'vue'
import type { UseQueryReturnType } from '@tanstack/vue-query'
import type {
  GlobalSearchResultType,
  GlobalSearchResponse,
  GlobalSearchResult,
} from '@lib/components/CGlobalSearch/CGlobalSearch.types'
import { type AppFile } from '@lib/types/app'

const KEY_UP_EVENT = 'keyup'
const SLASH_KEY_CODE = 'Slash'
const EXCLUDED_IDS = ['sentry-feedback']

export interface SearchAPI {
  query: Ref<string> | string
  includeExpired?: Ref<boolean> | boolean
}

export interface UseGlobalSearchParams<R> {
  debounceTime?: number
  initialSearchQuery?: string
  resultTypes?: GlobalSearchResultType[] | Ref<GlobalSearchResultType[]>
  includeExpired?: Ref<boolean> | boolean
  api: (params: SearchAPI) => UseQueryReturnType<R, unknown>
}

const useGlobalSearch = <R>({
  api,
  debounceTime = 250,
  resultTypes = ref([]),
  includeExpired,
}: UseGlobalSearchParams<R>) => {
  const search = ref('')
  const { data, isLoading: isApiLoading } = api({ query: search, includeExpired }) as {
    data: Ref<GlobalSearchResponse | undefined>
    isLoading: Ref<boolean>
  }
  const isSearchOpen = ref(false)
  const isWaitingForDebounce = ref(false)
  const isLoading = computed(() => isApiLoading.value || isWaitingForDebounce.value)

  const filteredData = computed(() => {
    if (!data.value || !unref(resultTypes)?.length) {
      return data.value
    }

    let filteredResults =
      data.value.results?.filter(
        (d) =>
          (unref(resultTypes)?.includes(d.type as GlobalSearchResultType) ||
            (d as unknown as AppFile).app_metadata?.module?.overlay_app?.enabled) &&
          !d.name?.startsWith('.'),
      ) || []

    // Sort 'app' type results, so overlay apps are shown first
    filteredResults = orderBy(
      filteredResults,
      (item: GlobalSearchResult) => !!(item as unknown as AppFile).app_metadata?.module?.overlay_app?.enabled,
      'desc',
    )

    const typeCounts = filteredResults.reduce(
      (counts, item) => {
        const type = item.type as GlobalSearchResultType
        counts[type] = (counts[type] || 0) + 1
        return counts
      },
      {} as Record<GlobalSearchResultType, number>,
    )

    const highestCount = Math.max(0, ...Object.values(typeCounts))

    return {
      ...data.value,
      highest_count: highestCount,
      overall_total: filteredResults.length,
      results: filteredResults,
    }
  })

  const debounceSearchUpdate = debounce((q: string) => {
    isWaitingForDebounce.value = false
    search.value = q
  }, debounceTime)

  const isOnlySlashKeyPressed = (event: KeyboardEvent) =>
    event.code === SLASH_KEY_CODE && !event.shiftKey && !event.ctrlKey && !event.altKey

  const isTargetAllowed = (event: KeyboardEvent) => {
    const target = event?.target as HTMLInputElement | null

    if (!target) {
      return true
    }

    if (EXCLUDED_IDS.includes(target.id)) return false

    const { tagName } = target

    if (!tagName) return false

    return !/^(?:input|textarea|select)$/i.test(tagName)
  }

  const openOnSlash = (event: KeyboardEvent) => {
    if (!isOnlySlashKeyPressed(event) || !isTargetAllowed(event) || isSearchOpen.value) {
      return
    }

    openSearch()
  }

  const openSearch = (open = true) => {
    isSearchOpen.value = open
  }

  const updateSearch = (query: string) => {
    isWaitingForDebounce.value = true
    debounceSearchUpdate(query)
  }

  onMounted(() => {
    document.addEventListener(KEY_UP_EVENT, openOnSlash)
  })

  onUnmounted(() => {
    document.removeEventListener(KEY_UP_EVENT, openOnSlash)
  })

  return {
    isSearchOpen,
    openSearch,
    data: filteredData,
    isLoading,
    search,
    updateSearch,
  }
}

export default useGlobalSearch
